import { PDFDocument, StandardFonts } from '@cantoo/pdf-lib';
import { downloadFile, getDateWithTimezoneFixed, readableDate } from '@chocolate-soup-inc/cs-api-consumer-utils';
import { getCountryName } from '@chocolate-soup-inc/cs-frontend-components';
import { addMonths, compareAsc, format, parse, startOfDay } from 'date-fns';
import _ from 'lodash';
import { quickScore } from 'quick-score';
import { useMemo } from 'react';
import CUSMAform from '../../generated/CUSMA.ups.flat.pdf';
import { TAddressParentTypes, TCarriers, TGiftFieldsFragment, TGiftTypes, TShipmentStatuses } from '../../generated/graphql';
import { getIconFromAddressParentType, getReadableAddressParentType } from '../addresses/shared';
import { getRemovedInfoFromRecipientData } from '../gifts/shared';
import { TShipmentType } from './queries';

export const CARRIER_OPTIONS: Record<string, string> = {
  [TCarriers.CanadaPost]: 'Canada Post',
  [TCarriers.Ups]: 'UPS',
  [TCarriers.PurolatorCa]: 'Purolator',
  [TCarriers.DhlExpressCanada]: 'DHL Express Canada'
};

export const CANADAPOST_SERVICE_OPTIONS: Record<string, string> = {
  expedited_parcel: 'Expedited Parcel',
  regular_parcel: 'Regular Parcel (Carbon Neutral)',
  xpresspost: 'Xpresspost',
  xpresspost_certified: 'Xpresspost Certified',
  priority: 'Priority',
  library_books: 'Library Books',
  expedited_parcel_usa: 'Expedited Parcel USA',
  priority_worldwide_envelope_usa: 'Priority Worldwide Envelope USA',
  priority_worldwide_pak_usa: 'Priority Worldwide pak USA',
  priority_worldwide_parcel_usa: 'Priority Worldwide Parcel USA',
  small_packet_air_usa: 'Small Packet Air - USA',
  tracked_packet_usa: 'Tracked Packet - USA',
  tracked_packet_usa_lvm: 'Tracked Packet - USA (LVM)',
  xpresspost_usa: 'Xpresspost USA',
  xpresspost_international: 'Xpresspost International',
  international_parcel_air: 'International Parcel Air',
  international_parcel_surface: 'International Parcel Surface',
  priority_worldwide_envelope_international: 'Priority Worldwide Envelope Intl',
  priority_worldwide_pak_international: 'Priority Worldwide pak Intl',
  priority_worldwide_parcel_international: 'Priority Worldwide parcel Intl',
  small_packet_international_air: 'Small Packet International Air',
  small_packet_international_surface: 'Small Packet International Surface',
  tracked_packet_international: 'Tracked Packet - International'
};

export const UPS_SERVICE_OPTIONS: Record<string, string> = {
  ups_worldwide_expedited: 'Worldwide Expedited',
  ups_standard: 'Standard',
  ups_standard_international: 'Standard International',
  ups_next_day_air_early_am: 'Express Early',
  ups_worldwide_express: 'Worldwide Express',
  ups_next_day_air: 'Express',
  ups_worldwide_express_plus: 'Worldwide Express Plus',
  ups_next_day_air_saver: 'Express Saver',
  ups_worldwide_saver: 'Worldwide Express Saver',
  ups_express_early_am: 'Express Early A.M. to the U.S.',
  ups_3_day_select: '3 Day Select to the U.S.'
};

export const PUROLATOR_SERVICE_OPTIONS: Record<string, string> = {
  purolator_ground: 'Ground',
  purolator_express_9am: 'Express 9 AM',
  purolator_express_1030am: 'Express 10:30 AM',
  purolator_express: 'Express',
  purolator_express_evening: 'Express Evening',
  purolator_ground9am: 'Ground 9AM',
  purolator_ground1030am: 'Ground 10:30AM',
  purolator_groundevening: 'Ground Evening',
  purolator_express_us: 'Express U.S.',
  purolator_express_us9am: 'Express U.S. 9AM',
  purolator_express_us1030am: 'Express U.S. 10:30AM',
  purolator_express_us1200: 'Express U.S. 12:00',
  purolator_groundus: 'Ground U.S.',
  purolator_express_international: 'Express International',
  purolator_express_international_9am: 'Express International 9AM',
  purolator_express_international_1030am: 'Express International 10:30 AM',
  purolator_express_international_1200: 'Express International 12:00',
  purolator_ground_distribution: 'Ground Distribution',
  purolator_quickship: 'QuickShip'
};

export const DHL_SERVICE_OPTIONS: Record<string, string> = {
  dhl_canada_express_worldwide: 'Express Worldwide',
  dhl_canada_express_worldwide_documents: 'Express Worldwide Documents',
  dhl_canada_express_worldwide_1200: 'Express 12:00',
  dhl_canada_express_worldwide_1200_documents: 'Express 12:00 Documents',
  dhl_canada_express_worldwide_1030: 'Express 10:30',
  dhl_canada_express_worldwide_1030_documents: 'Express 10:30 Documents',
  dhl_canada_express_worldwide_900: 'Express 9:00',
  dhl_canada_express_worldwide_900_documents: 'Express 9:00 Documents',
  dhl_canada_economy_select: 'Economy Select'
};

type TGetShipmentAddressInfoProps = {
  shipment: TShipmentType;
};

export type TShipmentAddressInfo = {
  addressTypeIcon?: string;
  readableAddressType?: string;
  recipientName?: string;
};

export const getShipmentAddressInfo = (props: TGetShipmentAddressInfoProps): TShipmentAddressInfo => {
  const { shipment } = props;
  const { company, office, employee } = shipment;

  const officeName = office?.name;
  const officeContactName = office?.contact?.name;
  const companyContactName = company?.contact?.name;

  const addressTypeIcon = getIconFromAddressParentType(shipment.address?.parentType);

  const readable = getReadableAddressParentType(shipment.address?.parentType);
  let readableAddressType: string | undefined = readable;
  if (readable === 'Office' && officeName) readableAddressType = officeName;
  else if (shipment.address?.parentType === TAddressParentTypes.Company) readableAddressType = 'Company HQ';

  let recipientName: string | undefined;
  if (shipment.address?.parentType === TAddressParentTypes.Employee) recipientName = employee?.fullName as string;
  else if (shipment.address?.parentType === TAddressParentTypes.Office) recipientName = officeContactName || `Office ${officeName}`;
  else if (shipment.address?.parentType === TAddressParentTypes.Company) recipientName = companyContactName || 'Company HQ';

  if (!recipientName)
    for (const g of shipment.gifts?.items || []) {
      if (!g || !g.recipientData) continue;
      recipientName = getRemovedInfoFromRecipientData(g);
    }

  return {
    addressTypeIcon,
    readableAddressType,
    recipientName
  };
};

export const useShipmentAddressInfo = (shipment?: TShipmentType) => {
  return useMemo(() => {
    if (shipment)
      return getShipmentAddressInfo({
        shipment
      });
    else return {};
  }, [shipment]);
};

type TUseShipmentsWithAddressInfoProps = {
  shipments: TShipmentType[];
};

export type TShipmentWithAddressInfo = TShipmentType & TShipmentAddressInfo;

export const useShipmentsWithAddressInfo = (props: TUseShipmentsWithAddressInfoProps) => {
  const { shipments } = props;

  return useMemo(() => {
    return shipments.map((s) => {
      return {
        ...s,
        ...getShipmentAddressInfo({ shipment: s })
      };
    });
  }, [shipments]);
};

type TRecipientFilterProps = {
  filter?: string;
  shipment: TShipmentWithAddressInfo;
};

export const filterByRecipient = (props: TRecipientFilterProps) => {
  const { shipment, filter } = props;

  if (filter == null || filter === '') return true;

  if (shipment.recipientName && quickScore(shipment.recipientName, filter) > 0.7) return true;

  for (const gift of shipment.gifts?.items || []) {
    if (!gift) continue;
    if (gift.recipient?.fullName && quickScore(gift.recipient.fullName, filter) > 0.7) return true;
  }

  return false;
};

type TGiftTypeFilterProps = {
  filter?: 'newHire' | 'subscription' | 'one-off';
  shipment: TShipmentWithAddressInfo;
};

export const filterByGiftType = (props: TGiftTypeFilterProps) => {
  const { shipment, filter } = props;

  if (filter == null) return true;

  for (const gift of shipment.gifts?.items || []) {
    if (!gift) continue;

    if (gift.type === filter) return true;
    else if (filter === 'subscription' && [TGiftTypes.Birthday, TGiftTypes.WorkAnniversary].includes(gift.type)) return true;
    else if (
      filter === 'one-off' &&
      [TGiftTypes.BabyGift, TGiftTypes.Bereavement, TGiftTypes.CarePackage, TGiftTypes.CustomBricks, TGiftTypes.Other, TGiftTypes.ReplacementEmployeeBirthday, TGiftTypes.ReplacementWorkAnniversary, TGiftTypes.Wedding].includes(gift.type)
    )
      return true;
  }

  return false;
};

export const getShipDate = (shipment: TShipmentType | undefined): Date | undefined => {
  if (!shipment) return undefined;

  if (!shipment.label) return undefined;

  const created = shipment.label.createdAt;
  if (!created) return undefined;

  return new Date(created);
};

export const getEventDate = (shipment: TShipmentType): Date | undefined => {
  const items = shipment.gifts?.items || [];
  if (items.length === 0) return undefined;

  const item = items[0] as TGiftFieldsFragment;
  const date = item.eventDate;
  if (!date) return undefined;

  // Preserve the event's calendar day before grouping or formatting it.
  return getDateWithTimezoneFixed(date);
};

export const getEventDateString = (shipment: TShipmentType): string => {
  const formatString = 'do MMM, yyyy';
  const items = Array.isArray(shipment.gifts) ? shipment.gifts : shipment.gifts?.items || [];

  const dates = items
    .map((item) => getDateWithTimezoneFixed(item.eventDate))
    .filter((date): date is Date => date != null)
    .map((date) => startOfDay(date))
    .sort(compareAsc);

  const uniqueDateStrings = _.uniqBy(dates, (date) => date.toString());

  if (dates.length === 0) return '-';

  if (dates.length === 1) return readableDate(dates[0], formatString);

  if (uniqueDateStrings.length === 1) {
    const a = readableDate(uniqueDateStrings[0], formatString);
    return `${items.length} events on ${a}`;
  }

  const a = readableDate(dates[0], formatString);
  const b = readableDate(dates[dates.length - 1], formatString);
  return `${items.length} events between ${a} and ${b}`;
};

export const getShipDateOnlyString = (value?: string | Date | null): string | undefined => {
  if (!value) return undefined;
  if (typeof value === 'string') return value.slice(0, 10);
  return format(value, 'yyyy-MM-dd');
};

export const matchesShipDateFilter = (shipment: TShipmentType, filterDate?: Date): boolean => {
  if (!filterDate) return true;
  return getShipDateOnlyString(filterDate) === getShipDateOnlyString(shipment.actualShippingDate || shipment.shippingDate);
};
export const parseShipDateFilterValue = (value?: unknown): Date | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) return value;
  if (typeof value === 'string') {
    const parsed = parse(value, 'MMM-d-yyyy', new Date());
    return isNaN(parsed.getTime()) ? undefined : parsed;
  }
  return undefined;
};

export const generateCUSMAformPDF = async (shipment: TShipmentType) => {
  if (
    ![
      //TShipmentStatuses.ReadyToShip,
      //TShipmentStatuses.PrintingLabel,
      TShipmentStatuses.LabelPrinted,
      TShipmentStatuses.Shipped
    ].includes(shipment.status) ||
    !['usa'].includes(shipment.address?.country?.toLowerCase() as string)
  )
    return;
  const response = await fetch(CUSMAform);
  const bytes = await response.arrayBuffer();
  const doc = await PDFDocument.load(bytes);
  const pages = doc.getPages();
  const font = await doc.embedFont(StandardFonts.Helvetica),
    size = 12,
    lineHeight = size + 2;
  /* const form = doc.getForm();
  form.getFields().forEach((f) => {
    const rect = f.acroField.getWidgets()[0].getRectangle();
    //console.log(`"${f.getName()}" (${f.constructor.name}): x=${Math.round(rect.x)}, y=${Math.round(rect.y)}, width=${Math.round(rect.width)}, height=${Math.round(rect.height)}`);
  }); */
  // The form doesn't flatten, so up till that gets sorted out, it's just easier to fill manually the fields
  /* form.getCheckBox('Check Box 19').uncheck(); // Importer
  form.getCheckBox('Check Box 20').check(); // Exporter
  form.getCheckBox('Check Box 21').uncheck(); // Producer */
  //form.getTextField('Blanket period FROM').setText(format(new Date(), 'dd/MM/yyyy'));
  pages[0].drawText(format(new Date(), 'dd/MM/yyyy'), { x: 350, y: 658, size, font });
  //form.getTextField('Blanket Period TO').setText(format(addMonths(new Date(), 2), 'dd/MM/yyyy'));
  pages[0].drawText(format(addMonths(new Date(), 2), 'dd/MM/yyyy'), { x: 350, y: 620, size, font });
  const CERTIFIED = [`Chocolate Soup Inc.`, `550 Parkside Dr.`, `Unit A23`, `Ontario (N2L5V4)`, `CANADA`];
  //form.getTextField('3 Certified').setText(CERTIFIED.join('\n'));
  let CERTIFIED_y = 556;
  CERTIFIED.forEach((txt) => {
    pages[0].drawText(txt, { x: 40, y: CERTIFIED_y, size, font });
    CERTIFIED_y -= lineHeight;
  });
  //form.getTextField('4 Exporter').setText('');
  pages[0].drawText(``, { x: 324, y: 556, size, font });
  //form.getTextField('5 Producer').setText('');
  pages[0].drawText(``, { x: 40, y: 436, size, font });
  const IMPORTER = [
    `${shipment.recipientName ?? shipment.company?.name ?? '-'}`,
    `${shipment.address?.address1 ?? ''} ${shipment.address?.address2 ?? ''}`,
    `${shipment.address?.city ?? ''}, ${shipment.address?.state ?? ''}, ${shipment.address?.country}. (${shipment.address?.zipCode ?? ''})`,
    `${shipment.employee?.phoneNumber ?? ''}`,
    `${shipment.employee?.email ?? ''}`
  ];
  //form.getTextField('6 Importer').setText(IMPORTER.join('\n'));
  let IMPORTER_y = 436;
  IMPORTER.forEach((txt) => {
    pages[0].drawText(txt, { x: 323, y: IMPORTER_y, size, font });
    IMPORTER_y -= lineHeight;
  });
  /* form.getTextField('7 Description').setText('');
  form.getTextField('7 HS#').setText('');
  form.getTextField('8 Origina Criterion').setText(''); */
  //form.getTextField('9 Date').setText('Dan Silivestru - CEO');
  pages[0].drawText(``, { x: 42, y: 66, size, font });
  //form.getTextField('Date 19').setText(format(new Date(), 'dd/MM/yyyy'));
  pages[0].drawText(format(new Date(), 'dd/MM/yyyy'), { x: 466, y: 66, size, font });
  /* form.getTextField('Page 3 7 Description').setText('');
  form.getTextField('Page 3 7 HS#').setText('');
  form.getTextField('Page 3 8 Origin').setText(''); */
  const descriptionText: string[] = [],
    hsText: string[] = [],
    originText: string[] = [],
    descriptionTextNext: string[] = [],
    hsTextNext: string[] = [],
    originTextNext: string[] = [];
  let descriptionText_y = 304,
    hsText_y = 304,
    originText_y = 304,
    descriptionTextNext_y = 632,
    hsTextNext_y = 632,
    originTextNext_y = 632;
  let i = 0;
  shipment.customsData?.forEach((product) => {
    if (!product) return;
    if (!['CAN', 'USA', 'MEX'].includes(product.country as string)) return;
    if (!product.originCriterion) return;
    if (i <= 8) {
      descriptionText.push(`${product.description ?? '-'}`);
      hsText.push(`${product.hsCode ?? '-'}`);
      originText.push(`${product.country != 'USA' ? getCountryName(product.country) : 'USA'} - ${product.originCriterion ?? ''}`);
    } else {
      descriptionTextNext.push(`${product.description ?? '-'}`);
      hsTextNext.push(`${product.hsCode ?? '-'}`);
      originTextNext.push(`${product.country != 'USA' ? getCountryName(product.country) : 'USA'} - ${product.originCriterion ?? ''}`);
    }
    i++;
  });
  //form.getTextField('7 Description').setText(descriptionText.join('\n'));
  descriptionText.forEach((txt) => {
    pages[0].drawText(txt, { x: 42, y: descriptionText_y, size, font });
    descriptionText_y -= lineHeight;
  });
  //form.getTextField('7 HS#').setText(hsText.join('\n'));
  hsText.forEach((txt) => {
    pages[0].drawText(txt, { x: 323, y: hsText_y, size, font });
    hsText_y -= lineHeight;
  });
  //form.getTextField('8 Origina Criterion').setText(originText.join('\n'));
  originText.forEach((txt) => {
    pages[0].drawText(txt, { x: 396, y: originText_y, size, font });
    originText_y -= lineHeight;
  });
  //form.getTextField('Page 3 7 Description').setText(descriptionTextNext.join('\n'));
  descriptionTextNext.forEach((txt) => {
    pages[1].drawText(txt, { x: 42, y: descriptionTextNext_y, size, font });
    descriptionTextNext_y -= lineHeight;
  });
  //form.getTextField('Page 3 7 HS#').setText(hsTextNext.join('\n'));
  hsTextNext.forEach((txt) => {
    pages[1].drawText(txt, { x: 316, y: hsTextNext_y, size, font });
    hsTextNext_y -= lineHeight;
  });
  //form.getTextField('Page 3 8 Origin').setText(originTextNext.join('\n'));
  originTextNext.forEach((txt) => {
    pages[1].drawText(txt, { x: 396, y: originTextNext_y, size, font });
    originTextNext_y -= lineHeight;
  });
  /* form.updateFieldAppearances();
  form.getFields().forEach((f) => f.enableReadOnly()); */
  //form.flatten({ updateFieldAppearances: true });
  doc.encrypt({
    ownerPassword: shipment.trackingNumber ?? `CS${format(new Date(), 'yyyy')}`,
    permissions: {
      copying: false,
      contentAccessibility: true,
      fillingForms: false,
      modifying: false,
      printing: 'lowResolution',
      annotating: false,
      documentAssembly: false
    }
  });
  // Format the AWSDate before it can be coerced to UTC.
  const altName = `${shipment.company?.name ?? ''} ${readableDate(shipment.actualShippingDate || shipment.shippingDate || new Date(), 'dd-MM-yyyy')} ${shipment.recipientName ?? ''}`;
  downloadFile({
    data: [await doc.save()] as BlobPart[],
    filetype: 'application/pdf',
    filename: `${shipment.trackingNumber ?? altName} - CUSMA.pdf`
  });
};
